/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {datadogRum, RumInitConfiguration} from '@datadog/browser-rum';
import {clone, get} from '../environment/environment';
import {
  logIdentifyUserInitializationError,
  logIdentifyUserSuccess,
  logInitializeAvailableError,
  logInitializeConfigError,
  logInitializeSuccess,
  logReportPageCrashFailure,
  logReportPageCrashSuccess
} from './DatadogLogger';

declare const globalThis: any;

/**
 * Defines the default RUM session sample rate (percentage of sessions for
 * which RUM data is collected) applied when an explicit rate is not provided.
 */
const DEFAULT_SESSION_SAMPLE_RATE = 100;

/**
 * Defines the default Session Replay sample rate (percentage of collected
 * sessions for which a replay recording is captured) applied when an explicit
 * rate is not provided.
 */
const DEFAULT_REPLAY_SAMPLE_RATE = 20;

/**
 * Defines the default backend trace sample rate (percentage of RUM resources
 * for which a backend APM trace is retained) applied when an explicit rate is
 * not provided.
 */
const DEFAULT_TRACE_SAMPLE_RATE = 100;

/**
 * Defines the first-party domain used to restrict trace header injection to
 * iOn backend service origins.
 */
const CALAMP_DOMAIN = '.calamp.com';

/**
 * Defines the substring (case-insensitive) used to identify environment
 * configuration keys that hold a backend service URL.
 */
const SERVICE_URL_KEY = 'serviceurl';

/**
 * Defines the trace context propagators injected into requests to allowed
 * tracing URLs, correlating RUM sessions with their backend APM traces.
 */
const DATADOG_PROPAGATORS: ('tracecontext' | 'datadog')[] = [
  'tracecontext', 'datadog'
];

/**
 * Provides the type definition for the optional user properties that can be
 * sent to Datadog as RUM user attributes alongside the user identity. These
 * properties appear as filterable attributes in the Datadog RUM dashboard.
 */
export interface DatadogUserProperties {
  userId?: number;
  username?: string;
  accountName?: string;
  accountId?: number;
}

/**
 * Provides the type definition for the runtime configuration used to
 * initialize the Datadog Browser RUM SDK. The `applicationId`, `clientToken`,
 * and `site` are sourced from the Datadog RUM application, while the
 * `allowedTracingUrls` define the first-party backend origins for which
 * distributed trace headers are injected to correlate RUM sessions with
 * backend APM traces.
 *
 * Each of the `applicationId`, `clientToken`, and `site` is optional and is
 * resolved, in order of precedence, from the following sources:
 *
 * 1.) The explicit value provided on the configuration
 * 2.) The `datadogApplicationId`, `datadogClientToken`, or `datadogSite` entry
 *     in `environment.json`, which is loaded at runtime
 * 3.) The `NEXT_PUBLIC_DATADOG_APPLICATION_ID`,
 *     `NEXT_PUBLIC_DATADOG_CLIENT_TOKEN`, or `NEXT_PUBLIC_DATADOG_SITE`
 *     environment variable, which is no longer populated by either
 *     application's build and remains only as an explicit override
 *
 * A value which is empty at one source falls through to the next source.
 */
export interface DatadogConfig {
  applicationId?: string;
  clientToken?: string;
  site?: string;
  service: string;
  env: string;
  version?: string;
  sessionSampleRate?: number;
  sessionReplaySampleRate?: number;
  traceSampleRate?: number;
}

/**
 * The Datadog API provides the single point of entry for all Datadog RUM
 * (Real User Monitoring) integrations and configuration.
 *
 * The process of integrating Datadog within iOn Applications is as follows:
 *
 * 1.) Invoke `Datadog.initialize(config)` to initialize the Browser RUM SDK
 * 2.) Upon successful user authentication, invoke `Datadog.identifyUser(props)`
 *     with the user's `userId`, `username`, `accountName`, and `accountId` to
 *     associate the RUM session with the authenticated user as filterable user
 *     attributes in the Datadog RUM dashboard
 *
 * ```typescript
 *  import Datadog from '@ion/core/src/analytics/Datadog';
 *
 *  // initialize the Browser RUM SDK once configuration is available ...
 *  Datadog.initialize(config);
 *
 *  // upon successful user authentication, associate the RUM session ...
 *  Datadog.identifyUser({
 *    userId      : user.id,
 *    username    : user.username,
 *    accountName : user.account?.title,
 *    accountId   : accountId
 *  });
 * ```
 */
class Datadog {
  /**
   * @private
   */
  static _initialized = false;

  /**
   * @private
   *
   * Defines the User Properties sent to the Datadog RUM SDK for tracking user
   * specific metadata, including username, id, account name, and account id.
   */
  static _properties: DatadogUserProperties = {};

  /**
   * Indicates whether Datadog RUM has been successfully initialized and the
   * Browser RUM SDK is available for use. This method should be used to guard
   * calls to other Datadog methods when invocation should be conditional.
   *
   * ```typescript
   * import Datadog from '@ion/core/src/analytics/Datadog';
   *
   *  if (Datadog.isEnabled()) {
   *    Datadog.identifyUser({userId: user.id});
   *  }
   * ```
   *
   * @returns `true` if `Datadog.initialize()` has been called and the Browser
   *          RUM SDK is available, otherwise `false`.
   */
  static isEnabled(): boolean {
    return Datadog._initialized;
  }

  /**
   * Initializes the Datadog Browser RUM SDK with the provided configuration and
   * begins Session Replay recording. The SDK self-manages all automatic
   * telemetry collection (page views, resources, long tasks, user interactions,
   * and unhandled errors) once initialized. This method must be called once at
   * application startup, mirroring the pattern used by `Pendo.injectScript()`.
   *
   * The `applicationId`, `clientToken`, and `site` are each resolved, in order
   * of precedence, from the explicit value on the provided configuration, the
   * `datadogApplicationId`, `datadogClientToken`, or `datadogSite` entry in
   * `environment.json` loaded at runtime, and finally the
   * `NEXT_PUBLIC_DATADOG_APPLICATION_ID`, `NEXT_PUBLIC_DATADOG_CLIENT_TOKEN`,
   * or `NEXT_PUBLIC_DATADOG_SITE` environment variable, which neither
   * application's build populates any longer and which remains only as an
   * explicit override. An empty value falls through to the next source. If any
   * of the three remains unresolved, a configuration error is logged and the
   * SDK is not initialized.
   *
   * ```typescript
   *
   *   import Datadog from '@ion/core/src/analytics/Datadog';
   *
   *   Datadog.initialize({
   *     applicationId : config.datadogApplicationId,
   *     clientToken   : config.datadogClientToken,
   *     site          : 'datadoghq.com',
   *     service       : 'ion-suite',
   *     env           : getEnvironment()
   *   });
   *
   * ```
   *
   * @param config The runtime configuration used to initialize the RUM SDK.
   */
  static initialize(config: DatadogConfig): boolean {
    if (!Datadog._initialized) {
      const {applicationId, clientToken, site} = resolveConfig(config);
      if (applicationId && clientToken && site) {
        datadogRum.init({
          applicationId,
          clientToken,
          site,
          service                 : config?.service,
          env                     : config?.env,
          version                 : config?.version,
          sessionSampleRate       : config?.sessionSampleRate ?? DEFAULT_SESSION_SAMPLE_RATE,
          sessionReplaySampleRate : config?.sessionReplaySampleRate ?? DEFAULT_REPLAY_SAMPLE_RATE,
          traceSampleRate         : config?.traceSampleRate ?? DEFAULT_TRACE_SAMPLE_RATE,
          defaultPrivacyLevel     : 'mask-user-input',
          allowedTracingUrls      : getTracingUrls(),
          trackUserInteractions   : true,
          trackResources          : true,
          trackLongTasks          : true
        });
        datadogRum.startSessionReplayRecording();
        logInitializeSuccess();
        return (Datadog._initialized = true);
      } else {
        logInitializeConfigError();
      }
    } else {
      logInitializeAvailableError();
    }
    return false;
  }

  /**
   * Associates the current RUM session with the authenticated user's identity
   * and sends user properties (userId, username, account name, accountId) as
   * filterable user attributes in the Datadog RUM dashboard. The properties are
   * persisted internally for inclusion in subsequent `reportPageCrash` events.
   * Must be called after successful user authentication, mirroring the pattern
   * used by `Pendo.initialize(user)`.
   *
   * ```typescript
   *   import Datadog from '@ion/core/src/analytics/Datadog';
   *
   *   // upon successful user authentication ...
   *   Datadog.identifyUser({
   *     userId      : user.id,
   *     username    : user.username,
   *     accountName : user.account?.title,
   *     accountId   : accountId
   *   });
   * ```
   *
   * @param properties The user properties to identify and persist.
   */
  static identifyUser(properties: DatadogUserProperties): boolean {
    if (Datadog._initialized) {
      Datadog._properties = {...properties};
      datadogRum.setUser(createUser(properties));
      logIdentifyUserSuccess();
      return true;
    }
    logIdentifyUserInitializationError();
    return false;
  }

  /**
   * Reports a page crash event to Datadog RUM as a custom error categorized by
   * an `errorType` of `Page Crash`. The error message and stacktrace are
   * captured automatically from the `Error` instance, while the authenticated
   * user identity (persisted by `identifyUser()`) and the current page are
   * attached as filterable error context.
   *
   * ```typescript
   *  import Datadog from '@ion/core/src/analytics/Datadog';
   *
   *  try {
   *     ...
   *  } catch (err) {
   *    Datadog.reportPageCrash(err);
   *  }
   * ```
   *
   * @param error The Error instance that caused the page crash.
   */
  static reportPageCrash(error: Error): boolean {
    if (Datadog._initialized) {
      const title = globalThis?.document?.title || '';
      const href  = globalThis?.location?.href || 'unknown';
      const user  = createUserTag();
      const page  = title ? `${title} [${href}]` : href;
      datadogRum.addError(error, {
        errorType : 'Page Crash',
        page,
        user
      });
      logReportPageCrashSuccess();
      return true;
    }
    logReportPageCrashFailure();
    return false;
  }
};

/**
 * @private
 *
 * Resolves the Datadog RUM `applicationId`, `clientToken`, and `site` from the
 * first source which provides a non-empty value, in order of precedence: the
 * explicit value on the provided configuration, the corresponding
 * `environment.json` entry loaded at runtime, and the `NEXT_PUBLIC_DATADOG_*`
 * environment variable, which neither application's build populates any longer
 * and which remains only as an explicit override. A value which is empty at
 * every source resolves to an empty string. The `process.env` references are
 * literal as Next.js only inlines `NEXT_PUBLIC_*` variables for literal
 * property access.
 *
 * @param   config The runtime configuration provided to `initialize()`.
 * @returns The resolved `applicationId`, `clientToken`, and `site`.
 */
const resolveConfig = (
  config: DatadogConfig): Record<'applicationId' | 'clientToken' | 'site', string> => ({
  applicationId : config?.applicationId || get('datadogApplicationId') ||
    process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
  clientToken : config?.clientToken || get('datadogClientToken') ||
    process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
  site : config?.site || get('datadogSite') ||
    process.env.NEXT_PUBLIC_DATADOG_SITE || ''
});

/**
 * @private
 *
 * Builds the Datadog `allowedTracingUrls` from the loaded environment
 * configuration, selecting every entry whose key contains `serviceURL`
 * (case-insensitive) and whose value is a first-party `.calamp.com` origin.
 * Requests to these origins inject distributed trace headers (using the
 * `tracecontext` and `datadog` propagators) so that RUM sessions correlate
 * with their backend APM traces.
 *
 * @returns The list of allowed tracing URL matchers for the RUM SDK.
 */
const getTracingUrls = (): RumInitConfiguration['allowedTracingUrls'] => (
  Object.entries(clone()).filter(([key, value]) => (
    key.toLowerCase().includes(SERVICE_URL_KEY) &&
    typeof value === 'string' &&
    value.includes(CALAMP_DOMAIN)
  )).map(([, value]) => ({
    match           : value as string,
    propagatorTypes : DATADOG_PROPAGATORS
  }))
);

/**
 * @private
 *
 * Creates a Datadog RUM user object from the provided user properties. Only
 * non-empty values are included in the resulting user object. The `userId` is
 * mapped to the RUM `id` and the `username` to the RUM `name`.
 *
 * @param   properties The user properties to convert.
 * @returns A Datadog RUM user object containing the defined attributes.
 */
const createUser = (
  properties: DatadogUserProperties): Record<string, string> => {
  const result: Record<string, string> = {};
  const {userId, username, accountName, accountId} = properties || {};
  if (userId) {
    result.id = `${userId}`;
  }
  if (username) {
    result.name = username;
  }
  if (accountName) {
    result.accountName = accountName;
  }
  if (accountId) {
    result.accountId = `${accountId}`;
  }
  return result;
};

/**
 * @private
 *
 * Creates a formatted user tag string from the persisted username and userId.
 * Returns `"username (userId)"` when both are available, just `userId` when
 * only the userId is set, or an empty string if neither has been set.
 *
 * @returns A formatted user tag string, or an empty string.
 */
const createUserTag = (): string => {
  const {userId} = Datadog?._properties || {};
  if (userId) {
    const {username, accountName, accountId} = Datadog._properties;
    const user    = username ? `${username} (${userId})` : `${userId}`;
    const account = (accountName && accountId) ? ` [${accountName}: ${accountId}]` : '';
    return `${user}${account}`;
  }
  return '';
};

/**
 * @private
 *
 * Provides a means of resetting the initialized state and persisted user
 * properties. This method should only be used for testing purposes.
 */
export const __reset = () => {
  Datadog._initialized = false;
  Datadog._properties  = {};
};

export default Datadog;
